import {Injectable} from '@angular/core';
import {
  IAvailableTime,
  IAvailableTimeForm,
  IDevices,
  IDevicesBranch,
  IDevicesForm,
  IUnitSuspiciousActivityConfig,
  IUnitSuspiciousActivityConfigForm,
  IUnitSuspiciousActivityLevel1,
  IUnitSuspiciousActivityLevel1Form,
  IUnitSuspiciousActivityLevel2,
  IUnitSuspiciousActivityLevel2Form,
  IUnitSuspiciousActivityLevel3,
  IUnitSuspiciousActivityLevel3Form,
  IUnitSuspiciousActivityQrCode,
  IUnitSuspiciousActivityQrCodeForm,
  IUnit,
  IUnitForm,
  IUnitUpdatePayload,
  IUsersBranch
} from "../../../../../utils/interfaces/unit";
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {PollTypeEnum} from "../../../../../utils/enums/poll-type.enum";

@Injectable()
export class UnitEditFormService {

  constructor() {
  }

  availableTimeCreate(availableTime: IAvailableTime): FormGroup<IAvailableTimeForm> {
    const available = new FormGroup<IAvailableTimeForm>({
      description: new FormControl<string>(
        {
          value: availableTime.description,
          disabled: true,
        },
        {validators: Validators.required}
      ),
      startTime: new FormControl<string>(
        {
          value: availableTime.startTime,
          disabled: true,
        },
        {validators: Validators.required}
      ),
      endTime: new FormControl<string>(
        {
          value: availableTime.endTime,
          disabled: true,
        },
        {validators: Validators.required}
      ),
      typePoll: new FormControl<PollTypeEnum | null>(
        {
          value: availableTime.typePoll,
          disabled: true,
        }, Validators.required),
    });

    if (availableTime.id) {
      available.addControl(
        'id',
        new FormControl<string | null>(availableTime.id)
      );
    }

    return available;
  }

  deviceCreate(device: IDevices): FormGroup<IDevicesForm> {
    const deviceForm = new FormGroup<IDevicesForm>({
      name: new FormControl<string>(
        {value: device.name, disabled: true},
        Validators.required
      ),
      typePoll: new FormControl<PollTypeEnum | null>(
        {value: device.typePoll, disabled: true},
        Validators.required
      ),
    });

    if (device.id) {
      deviceForm.addControl('id', new FormControl<string | null>(device.id));
    }

    return deviceForm;
  }

  createSuspiciousActivityConfigForm(
    suspiciousActivityConfig?: IUnitSuspiciousActivityConfig | null
  ): FormGroup<IUnitSuspiciousActivityConfigForm> {
    const config = this.resolveSuspiciousActivityConfig(suspiciousActivityConfig);

    const suspiciousActivityConfigForm = new FormGroup<IUnitSuspiciousActivityConfigForm>({
      level1: this.createSuspiciousActivityLevel1Form(config.level1),
      level2: this.createSuspiciousActivityLevel2Form(config.level2),
      level3: this.createSuspiciousActivityLevel3Form(config.level3),
      qrCode: this.createSuspiciousActivityQrCodeForm(config.qrCode),
    });

    this.syncSuspiciousActivityConfigState(suspiciousActivityConfigForm);

    return suspiciousActivityConfigForm;
  }

  patchSuspiciousActivityConfigForm(
    suspiciousActivityConfigForm: FormGroup<IUnitSuspiciousActivityConfigForm>,
    suspiciousActivityConfig?: IUnitSuspiciousActivityConfig | null
  ): void {
    suspiciousActivityConfigForm.patchValue(
      this.resolveSuspiciousActivityConfig(suspiciousActivityConfig),
      {emitEvent: false}
    );
    this.syncSuspiciousActivityConfigState(suspiciousActivityConfigForm);
  }

  syncSuspiciousActivityConfigState(
    suspiciousActivityConfigForm: FormGroup<IUnitSuspiciousActivityConfigForm>
  ): void {
    this.syncLevel1Validators(suspiciousActivityConfigForm.controls.level1);
    this.syncLevel2Validators(suspiciousActivityConfigForm.controls.level2);
    this.syncLevel3Availability(suspiciousActivityConfigForm);
  }

  syncLevel1Validators(level1Form: FormGroup<IUnitSuspiciousActivityLevel1Form>): void {
    this.syncLevelState(level1Form, [
      'consecutiveResponsesThreshold',
      'observationWindowMinutes',
      'messageDisplayMinutes',
      'level1RecurrenceThreshold',
    ]);
  }

  syncLevel2Validators(level2Form: FormGroup<IUnitSuspiciousActivityLevel2Form>): void {
    this.syncLevelState(level2Form, [
      'alertPercentageThreshold',
      'quantityWhenNoAverage',
      'observationWindowMinutes',
      'messageDisplayMinutes',
      'level2RecurrenceThreshold',
    ]);
  }

  syncLevel3Validators(level3Form: FormGroup<IUnitSuspiciousActivityLevel3Form>): void {
    this.syncLevelState(level3Form, ['blockDurationMinutes']);
  }

  formToUnitValue(form: FormGroup<IUnitForm>): IUnit {
    return {
      ...this.formToUnitUpdateValue(form),
      suspiciousActivityConfig: this.formToSuspiciousActivityConfigValue(form),
    };
  }

  formToUnitUpdateValue(form: FormGroup<IUnitForm>): IUnitUpdatePayload {
    const availableShifts = [
      ...form.controls.satisfactionAvailableShifts.getRawValue() as IAvailableTime[],
      ...form.controls.reservationAvailableShifts.getRawValue() as IAvailableTime[],
    ];

    const devices = [
      ...form.controls.satisfactionDevices.getRawValue() as IDevicesBranch[],
      ...form.controls.reservationDevices.getRawValue() as IDevicesBranch[],
    ];

    return {
      id: form.controls.id.value!,
      branchId: form.controls.branchId.value!,
      code: form.controls.code.value!,
      name: form.controls.name.value!,
      region: form.controls.region.value!,
      customerName: form.controls.customerName.value!,
      shortBranch: form.controls.shortBranch.value!,
      pollId: form.controls.pollId.value!,
      pollDishReservationId: form.controls.pollDishReservationId.value!,
      availableShifts,
      active: form.controls.active.value!,
      usersBranch: form.controls.usersBranch.getRawValue() as IUsersBranch[],
      devices,
    };
  }

  formToSuspiciousActivityConfigValue(
    form: FormGroup<IUnitForm>
  ): IUnitSuspiciousActivityConfig {
    return this.suspiciousActivityConfigFormToValue(
      form.controls.suspiciousActivityConfig
    );
  }

  private defaultSuspiciousActivityConfig(): IUnitSuspiciousActivityConfig {
    return {
      level1: {
        active: false,
        consecutiveResponsesThreshold: null,
        observationWindowMinutes: null,
        messageDisplayMinutes: null,
        level1RecurrenceThreshold: null,
      },
      level2: {
        active: false,
        alertPercentageThreshold: null,
        quantityWhenNoAverage: null,
        observationWindowMinutes: null,
        messageDisplayMinutes: null,
        level2RecurrenceThreshold: null,
      },
      level3: {
        active: false,
        blockDurationMinutes: null,
      },
      qrCode: {
        minVoteIntervalMinutes: null,
      },
    };
  }

  private resolveSuspiciousActivityConfig(
    suspiciousActivityConfig?: IUnitSuspiciousActivityConfig | null
  ): IUnitSuspiciousActivityConfig {
    return suspiciousActivityConfig ?? this.defaultSuspiciousActivityConfig();
  }

  private createSuspiciousActivityLevel1Form(
    level1: IUnitSuspiciousActivityLevel1
  ): FormGroup<IUnitSuspiciousActivityLevel1Form> {
    return new FormGroup<IUnitSuspiciousActivityLevel1Form>({
      active: new FormControl<boolean | null>(level1.active),
      consecutiveResponsesThreshold: new FormControl<number | null>(level1.consecutiveResponsesThreshold),
      observationWindowMinutes: new FormControl<number | null>(level1.observationWindowMinutes),
      messageDisplayMinutes: new FormControl<number | null>(level1.messageDisplayMinutes),
      level1RecurrenceThreshold: new FormControl<number | null>(level1.level1RecurrenceThreshold),
    });
  }

  private createSuspiciousActivityLevel2Form(
    level2: IUnitSuspiciousActivityLevel2
  ): FormGroup<IUnitSuspiciousActivityLevel2Form> {
    return new FormGroup<IUnitSuspiciousActivityLevel2Form>({
      active: new FormControl<boolean | null>(level2.active),
      alertPercentageThreshold: new FormControl<number | null>(level2.alertPercentageThreshold),
      quantityWhenNoAverage: new FormControl<number | null>(level2.quantityWhenNoAverage),
      observationWindowMinutes: new FormControl<number | null>(level2.observationWindowMinutes),
      messageDisplayMinutes: new FormControl<number | null>(level2.messageDisplayMinutes),
      level2RecurrenceThreshold: new FormControl<number | null>(level2.level2RecurrenceThreshold),
    });
  }

  private createSuspiciousActivityLevel3Form(
    level3: IUnitSuspiciousActivityLevel3
  ): FormGroup<IUnitSuspiciousActivityLevel3Form> {
    return new FormGroup<IUnitSuspiciousActivityLevel3Form>({
      active: new FormControl<boolean | null>(level3.active),
      blockDurationMinutes: new FormControl<number | null>(level3.blockDurationMinutes),
    });
  }

  private createSuspiciousActivityQrCodeForm(
    qrCode: IUnitSuspiciousActivityQrCode
  ): FormGroup<IUnitSuspiciousActivityQrCodeForm> {
    return new FormGroup<IUnitSuspiciousActivityQrCodeForm>({
      minVoteIntervalMinutes: new FormControl<number | null>(qrCode.minVoteIntervalMinutes),
    });
  }

  private syncLevel3Availability(
    suspiciousActivityConfigForm: FormGroup<IUnitSuspiciousActivityConfigForm>
  ): void {
    const level1Active = suspiciousActivityConfigForm.controls.level1.controls.active.value ?? false;
    const level2Active = suspiciousActivityConfigForm.controls.level2.controls.active.value ?? false;
    const level3Form = suspiciousActivityConfigForm.controls.level3;
    const level3ActiveControl = level3Form.controls.active;
    const level3Available = level1Active || level2Active;

    if (level3Available) {
      level3ActiveControl.enable({emitEvent: false});
    } else {
      level3ActiveControl.setValue(false, {emitEvent: false});
      level3ActiveControl.disable({emitEvent: false});
    }

    this.syncLevel3Validators(level3Form);
  }

  private suspiciousActivityConfigFormToValue(
    suspiciousActivityConfigForm: FormGroup<IUnitSuspiciousActivityConfigForm>
  ): IUnitSuspiciousActivityConfig {
    const suspiciousActivityConfig = suspiciousActivityConfigForm.getRawValue();

    return {
      level1: {
        ...suspiciousActivityConfig.level1,
        active: suspiciousActivityConfig.level1.active ?? false,
      },
      level2: {
        ...suspiciousActivityConfig.level2,
        active: suspiciousActivityConfig.level2.active ?? false,
      },
      level3: {
        ...suspiciousActivityConfig.level3,
        active: suspiciousActivityConfig.level3.active ?? false,
      },
      qrCode: {...suspiciousActivityConfig.qrCode},
    };
  }

  private syncLevelState(levelForm: FormGroup, fieldNames: string[]): void {
    const isActive = ((levelForm.get('active') as FormControl<boolean | null>).value ?? false);

    fieldNames.forEach((fieldName) => {
      const control = levelForm.get(fieldName) as FormControl<number | null>;

      if (isActive) {
        control.enable({emitEvent: false});
        control.setValidators(Validators.required);
      } else {
        control.clearValidators();
        control.disable({emitEvent: false});
      }

      control.updateValueAndValidity({emitEvent: false});
    });
  }
}
